import React from 'react';
import { FormErrors } from '../../../types';
import { CategoryDropdown } from '../category';
import TagsBar, { Tag } from '../../../components/common/TagsBar';
import { RichTextEditor } from '../../../components/common';
import ContactSearchSection from '../contacts/ContactSearchSection';
import { PropertySearchSection } from '../properties/PropertySearchSection';
import ExpenseSearchSection from '../expenses/ExpenseSearchSection';
import { PropertyWithDetails } from '../properties';

export interface NoteFormData {
  summary: string;
  description: string;
  typeId: number;
  priorityId: number;
  completionDate: string;
}

interface NotesInfoSectionProps {
  noteData: NoteFormData;
  errors: FormErrors;
  onChange: (field: keyof NoteFormData, value: string | number) => void;
  tags: Tag[];
  onTagsChange: (tags: Tag[]) => void;
  onUnsavedChanges: (hasChanges: boolean) => void;
  contactIds: number[];
  onContactsChange: (contactIds: number[]) => void;
  selectedProperty: PropertyWithDetails | null;
  onPropertySelect: (property: PropertyWithDetails | null) => void;
  expenseIds: number[];
  onExpensesChange: (expenseIds: number[]) => void;
}

const NotesInfoSection: React.FC<NotesInfoSectionProps> = ({
  noteData,
  errors,
  onChange,
  tags,
  onTagsChange,
  onUnsavedChanges,
  contactIds,
  onContactsChange,
  selectedProperty,
  onPropertySelect,
  expenseIds,
  onExpensesChange
}) => {
  const handleInputChange = (field: keyof NoteFormData, value: string | number) => {
    onChange(field, value);
  };

  return (
    <div className="form-section-content">
      <div className="app-form-row">
        <div className="app-form-group">
          <label htmlFor="summary" className="app-form-label required">
            Summary
          </label>
          <input
            id="summary"
            type="text"
            className={`app-form-input ${errors.summary ? 'error' : ''}`}
            value={noteData.summary}
            onChange={(e) => handleInputChange('summary', e.target.value)}
            placeholder="Brief note summary"
          />
          {errors.summary && <div className="app-form-error">{errors.summary}</div>}
        </div>
      </div>

      <div className="app-form-row">
        <div className="app-form-group">
          <RichTextEditor
            id="description"
            value={noteData.description}
            onChange={(html) => handleInputChange('description', html)}
            placeholder="Detailed note description"
          />
        </div>
      </div>

      <div className="app-form-row">
        <div className="app-form-group">
          <label className="app-form-label">Tags</label>
          <TagsBar
            selectedTags={tags}
            onTagsChange={onTagsChange}
            placeholder="Add tags..."
            onUnsavedChanges={onUnsavedChanges}
          />
        </div>
      </div>

      <div className="app-form-row">
        <div className="app-form-group">
          <label htmlFor="type" className="app-form-label required">
            Note Type
          </label>
          <CategoryDropdown
            parentType="note"
            value={noteData.typeId}
            onChange={(value) => handleInputChange('typeId', value)}
            required={true}
            className={errors.typeId ? 'error' : ''}
          />
          {errors.typeId && <div className="app-form-error">{errors.typeId}</div>}
        </div>

        <div className="app-form-group">
          <label htmlFor="priority" className="app-form-label">
            Priority
          </label>
          <CategoryDropdown
            parentType="priority"
            value={noteData.priorityId}
            onChange={(value) => handleInputChange('priorityId', value)}
            required={false}
          />
        </div>
      </div>

      <div className="app-form-row">
        <div className="app-form-group">
          <label htmlFor="completionDate" className="app-form-label">
            Completion Date
          </label>
          <input
            id="completionDate"
            type="date"
            className="app-form-input"
            value={noteData.completionDate}
            onChange={(e) => handleInputChange('completionDate', e.target.value)}
          />
        </div>
      </div>

      {/* Contact Association */}
      <div className="app-form-row">
        <div className="app-form-group">
          <ContactSearchSection
            selectedContactIds={contactIds}
            onContactsChange={onContactsChange}
            label="Contacts"
            placeholder="Search contacts to associate with this note..."
          />
        </div>
      </div>

      {/* Property Association */}
      <div className="app-form-row">
        <div className="app-form-group">
          <PropertySearchSection
            selectedProperty={selectedProperty}
            onPropertySelect={onPropertySelect}
            label="Property"
            placeholder="Search properties to associate with this note..."
          />
        </div>
      </div>

      {/* Expense Association */}
      <div className="app-form-row">
        <div className="app-form-group">
          <ExpenseSearchSection
            selectedExpenseIds={expenseIds}
            onExpensesChange={onExpensesChange}
            label="Expenses"
            placeholder="Search expenses by description, date, or amount..."
          />
        </div>
      </div>

      {/* Error Display */}
      {errors.associations && (
        <div className="app-form-row">
          <div className="app-form-group">
            <div className="app-form-error">{errors.associations}</div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NotesInfoSection;

